import { resolveDisplayStatusLabel } from './resolveDisplayStatusLabel';
import {
  type DisplayStatusSortKey,
  type DisplayStatusTier1,
  type DisplayStatusTier1Result,
  type ResolveDisplayStatusTier1Input,
} from './types';

const TIER1_LABELS = {
  draft: 'Draft',
  in_progress: 'In Progress',
  completed: 'Completed',
} as const;

const VARIANT_TO_SORT_KEY: Record<string, DisplayStatusSortKey> = {
  draft: 'draft',
  sent: 'sent',
  viewed: 'viewed',
  in_progress: 'in_progress',
  paid: 'paid',
  partially_paid: 'partially_paid',
  signed: 'signed',
  completed: 'completed',
  submitted: 'submitted',
  declined: 'declined',
  failed: 'failed',
  expired: 'expired',
  voided: 'sent',
};

function resolveSortKey(input: ResolveDisplayStatusTier1Input): DisplayStatusSortKey | undefined {
  if (input.sortKey) {
    return input.sortKey;
  }
  if (input.state) {
    return resolveDisplayStatusLabel({
      state: input.state,
      viewer: input.viewer ?? 'business',
    }).sortKey;
  }
  if (input.variant) {
    return VARIANT_TO_SORT_KEY[input.variant];
  }
  return undefined;
}

function tierFromSortKey(sortKey: DisplayStatusSortKey): DisplayStatusTier1 {
  switch (sortKey) {
    case 'draft':
      return 'draft';
    case 'completed':
    case 'signed':
    case 'submitted':
    case 'declined':
    case 'expired':
      return 'completed';
    case 'sent':
    case 'viewed':
    case 'scheduled':
    case 'partially_signed':
    case 'awaiting_payment':
    case 'awaiting_your_signature':
    case 'awaiting_client_signature':
    case 'partially_paid':
    case 'in_progress':
    case 'paid':
    case 'overdue':
    case 'failed':
      return 'in_progress';
    default: {
      const _exhaustive: never = sortKey;
      return _exhaustive;
    }
  }
}

/**
 * Collapse projector sortKey / variant into a Tier 1 badge: Draft, In Progress, or Completed.
 * `paid` and `failed` are In Progress. Declined and Expired are Completed; the Tier 2 label
 * (`Declined`, `Expired`) stays on the projector `label`.
 * Voided falls through to Sent (In Progress); it is not a card label.
 */
export function resolveDisplayStatusTier1(
  input: ResolveDisplayStatusTier1Input
): DisplayStatusTier1Result {
  const sortKey = resolveSortKey(input);
  const tier = sortKey ? tierFromSortKey(sortKey) : 'draft';
  return { tier, label: TIER1_LABELS[tier] };
}
